import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Typography,
  Box,
  Pagination,
  CircularProgress,
} from "@mui/material";
import ReplayCard from "../components/ReplayCard";
import { useAuth } from "../contexts/AuthContext";
import GameFilters from "../components/filters/GameFilters";
import SEO from "../components/SEO";
import { readCache, writeCache, writeNoDataCache, isNoDataCache } from "../utils/cacheStorage";

const PUBLIC_GAMES_CACHE_TTL = 24 * 60 * 60 * 1000;        // 1 day
const PUBLIC_GAMES_FORMATS_TTL = 30 * 24 * 60 * 60 * 1000; // 30 days
const PUBLIC_GAMES_POKEMON_TTL = 7 * 24 * 60 * 60 * 1000;  // 7 days

const DEFAULT_FILTERS = {
  sortBy: "date DESC",
  playerFilter: "",
  ratingFilter: "all",
  dateFilter: "all",
  showSaved: "all",
  formatFilter: "all",
  pokemonFilter: "",
};

const getPublicGamesCacheKey = ({ page, limit, sortBy, playerFilter, ratingFilter, dateFilter, showSaved, formatFilter, pokemonFilter, userId }) =>
  `public-games-cache:${page}:${limit}:${sortBy}:${playerFilter}:${ratingFilter}:${dateFilter}:${showSaved}:${formatFilter}:${pokemonFilter}:${userId || "anon"}`;

const getPublicGamesFormatsCacheKey = () => "public-games-cache:formats";
const getPublicGamesPokemonCacheKey = () => "public-games-cache:pokemon-list";

function PublicGamesPage() {
  const [games, setGames] = useState([]);
  const [numGames, setNumGames] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(true);

  const [availableFormats, setAvailableFormats] = useState([]);
  const [isLoadingFormats, setIsLoadingFormats] = useState(true);

  const [availablePokemon, setAvailablePokemon] = useState([]);
  const [isLoadingPokemon, setIsLoadingPokemon] = useState(true);

  // Form state (before Apply is clicked)
  const [sortBy, setSortBy] = useState(DEFAULT_FILTERS.sortBy);
  const [playerFilter, setPlayerFilter] = useState(DEFAULT_FILTERS.playerFilter);
  const [ratingFilter, setRatingFilter] = useState(DEFAULT_FILTERS.ratingFilter);
  const [dateFilter, setDateFilter] = useState(DEFAULT_FILTERS.dateFilter);
  const [showSaved, setShowSaved] = useState(DEFAULT_FILTERS.showSaved);
  const [formatFilter, setFormatFilter] = useState(DEFAULT_FILTERS.formatFilter);
  const [pokemonFilter, setPokemonFilter] = useState(DEFAULT_FILTERS.pokemonFilter);

  const { currentUser } = useAuth();

  // Active filters (applied on fetchPublicGames)
  const [activeFilters, setActiveFilters] = useState({ ...DEFAULT_FILTERS });

  const fetchAvailableFormats = useCallback(async () => {
    try {
      setIsLoadingFormats(true);
      const cacheKey = getPublicGamesFormatsCacheKey();
      const cached = readCache(cacheKey, PUBLIC_GAMES_FORMATS_TTL);
      if (cached) {
        if (!isNoDataCache(cached)) setAvailableFormats(cached.formats || []);
        return;
      }
      const { data } = await axios.get("/api/games/formats");
      if (data?.formats) {
        writeCache(cacheKey, { formats: data.formats });
        setAvailableFormats(data.formats);
      } else {
        writeNoDataCache(cacheKey);
      }
    } catch (error) {
      console.error("Error fetching available formats:", error);
      writeNoDataCache(getPublicGamesFormatsCacheKey());
    } finally {
      setIsLoadingFormats(false);
    }
  }, []);

  const fetchAvailablePokemon = useCallback(async () => {
    try {
      setIsLoadingPokemon(true);
      const cacheKey = getPublicGamesPokemonCacheKey();
      const cached = readCache(cacheKey, PUBLIC_GAMES_POKEMON_TTL);
      if (cached) {
        if (!isNoDataCache(cached)) setAvailablePokemon(cached.pokemon || []);
        return;
      }
      const { data } = await axios.get("/api/pokemon", { params: { limit: 1010 } });
      if (data?.pokemon?.length) {
        const list = data.pokemon.map((p) => ({ name: p.name, displayName: p.displayName || p.name }));
        writeCache(cacheKey, { pokemon: list });
        setAvailablePokemon(list);
      } else {
        writeNoDataCache(cacheKey);
      }
    } catch (error) {
      console.error("Error fetching pokemon list:", error);
      writeNoDataCache(getPublicGamesPokemonCacheKey());
    } finally {
      setIsLoadingPokemon(false);
    }
  }, []);

  useEffect(() => {
    fetchAvailableFormats();
    fetchAvailablePokemon();
  }, [fetchAvailableFormats, fetchAvailablePokemon]);

  const fetchPublicGames = useCallback(
    async (page, filters) => {
      const cacheKey = getPublicGamesCacheKey({
        page,
        limit: itemsPerPage,
        ...filters,
        userId: currentUser?.uid,
      });

      try {
        setIsLoading(true);
        const cached = readCache(cacheKey, PUBLIC_GAMES_CACHE_TTL);
        if (cached) {
          if (!isNoDataCache(cached)) {
            setGames(cached.games || []);
            setNumGames(cached.total ?? 0);
          }
          return;
        }

        const { data } = await axios.get("/api/games", {
          params: {
            page,
            limit: itemsPerPage,
            sortBy: filters.sortBy,
            playerFilter: filters.playerFilter,
            ratingFilter: filters.ratingFilter,
            dateFilter: filters.dateFilter,
            showSaved: filters.showSaved,
            format: filters.formatFilter,
            pokemonFilter: filters.pokemonFilter,
            userId: currentUser?.uid,
          },
        });

        if (data) {
          const payload = { games: data.games || [], total: data.total || 0 };
          if (payload.games.length === 0) {
            writeNoDataCache(cacheKey);
          } else {
            writeCache(cacheKey, payload);
          }
          setGames(payload.games);
          setNumGames(payload.total);
        }
      } catch (error) {
        console.error("Error fetching public games:", error);
        writeNoDataCache(cacheKey);
        setGames([]);
        setNumGames(0);
      } finally {
        setIsLoading(false);
      }
    },
    [itemsPerPage, currentUser]
  );

  const applyFilters = () => {
    setCurrentPage(1);
    setActiveFilters({ sortBy, playerFilter, ratingFilter, dateFilter, showSaved, formatFilter, pokemonFilter });
  };

  const resetFilters = () => {
    setSortBy(DEFAULT_FILTERS.sortBy);
    setPlayerFilter(DEFAULT_FILTERS.playerFilter);
    setRatingFilter(DEFAULT_FILTERS.ratingFilter);
    setDateFilter(DEFAULT_FILTERS.dateFilter);
    setShowSaved(DEFAULT_FILTERS.showSaved);
    setFormatFilter(DEFAULT_FILTERS.formatFilter);
    setPokemonFilter(DEFAULT_FILTERS.pokemonFilter);
    setCurrentPage(1);
    setActiveFilters({ ...DEFAULT_FILTERS });
  };

  useEffect(() => {
    fetchPublicGames(currentPage, activeFilters);
  }, [currentPage, activeFilters, fetchPublicGames]);

  const totalPages = Math.ceil((numGames || 0) / itemsPerPage);

  return (
    <Box component="main" sx={{ padding: { xs: 1, sm: 2 } }}>
      <SEO
        title="Public Battle Replays"
        description={`Browse and watch ${numGames ? `${numGames} ` : ""}recent competitive Pokémon VGC matches. Learn from top players and discover new strategies.`}
        keywords="pokemon replays, vgc battles, competitive matches, pokemon showdown replays, vgc gameplay, public games"
      />
      <Typography variant="h4" component="h1" gutterBottom>
        Public Games
      </Typography>
      <Typography variant="subtitle1" component="p" sx={{ marginBottom: 2 }}>
        Total Replays:{" "}
        {isLoading ? <CircularProgress size={20} sx={{ ml: 1 }} /> : numGames}
      </Typography>

      <GameFilters
        sortBy={sortBy}
        onSortByChange={setSortBy}
        playerFilter={playerFilter}
        onPlayerFilterChange={setPlayerFilter}
        ratingFilter={ratingFilter}
        onRatingFilterChange={setRatingFilter}
        dateFilter={dateFilter}
        onDateFilterChange={setDateFilter}
        formatFilter={formatFilter}
        onFormatFilterChange={setFormatFilter}
        availableFormats={availableFormats}
        isLoadingFormats={isLoadingFormats}
        pokemonFilter={pokemonFilter}
        onPokemonFilterChange={setPokemonFilter}
        availablePokemon={availablePokemon}
        isLoadingPokemon={isLoadingPokemon}
        showSaved={showSaved}
        onShowSavedChange={setShowSaved}
        showSavedFilter={!!currentUser}
        onApply={applyFilters}
        onReset={resetFilters}
      />

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box sx={{ 
            display: "grid", 
            gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" }, 
            gap: 2 
          }}>
            {games.map((game) => (
              <ReplayCard key={game.replay_id} game={game} />
            ))}
          </Box>

          {totalPages > 1 && (
            <Box sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(_, value) => setCurrentPage(value)}
                color="primary"
                siblingCount={1}
                boundaryCount={1}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

export default PublicGamesPage;
